import { Badge, Group, Paper, Stack, Text } from '@mantine/core'
import { Link } from 'react-router-dom'

import { deduplicationKindConfig, type DeduplicationKind } from '../config'
import type { RunSummary } from '../types'
import { formatDateTime, formatDuration, formatRelative } from '../utils/format'
import { runDetailPath } from '../utils/paths'
import { RunStatusBadge } from './StatusBadge'

type Props = {
  kind: DeduplicationKind
  run: RunSummary
}

export const RunSummaryCard = ({ kind, run }: Props) => {
  const kindConfig = deduplicationKindConfig[kind]
  const failedGroups = run.groupsFailed ?? run.statsJson?.groupsFailed ?? 0
  const terminals = run.terminals ?? {}
  const progressPct =
    run.groupsTotal && run.groupsTotal > 0
      ? Math.round((run.groupsProcessed / run.groupsTotal) * 100)
      : null

  return (
    <Paper withBorder p='lg' mb='lg'>
      <Group justify='space-between' mb='md'>
        <Group>
          <RunStatusBadge status={run.status} />
          {run.dryRun && (
            <Badge color='orange' variant='filled'>
              Dry run
            </Badge>
          )}
          <Text fw={600} size='lg'>
            Run {run.runId.slice(0, 8)}…
          </Text>
          <Badge variant='outline'>{run.triggerType}</Badge>
        </Group>
        <Text c='dimmed' size='sm'>
          Started {formatRelative(run.startedAt)} · {formatDateTime(run.startedAt)}
        </Text>
      </Group>

      <Group grow mb='md'>
        <Stack gap={4}>
          <Text size='sm' c='dimmed'>
            Groups processed
          </Text>
          <Text fw={600}>
            {run.groupsProcessed}
            {run.groupsTotal != null ? ` / ${run.groupsTotal}` : ''}
            {progressPct != null ? ` (${progressPct}%)` : ''}
          </Text>
        </Stack>
        <Stack gap={4}>
          <Text size='sm' c='dimmed'>
            {kindConfig.entityCountLabel}
          </Text>
          <Text fw={600}>{run.companiesTotal ?? '—'}</Text>
        </Stack>
        <Stack gap={4}>
          <Text size='sm' c='dimmed'>
            Duration
          </Text>
          <Text fw={600}>{formatDuration(run.durationMs)}</Text>
        </Stack>
        <Stack gap={4}>
          <Text size='sm' c='dimmed'>
            Failed groups
          </Text>
          <Text fw={600} c={failedGroups > 0 ? 'red' : undefined}>
            {failedGroups}
          </Text>
        </Stack>
        <Stack gap={4}>
          <Text size='sm' c='dimmed'>
            {kindConfig.reducedLabel}
          </Text>
          <Text fw={600}>{run.companiesReduced ?? 0}</Text>
        </Stack>
      </Group>

      {Object.keys(terminals).length > 0 && (
        <Group gap='xs' mb='md'>
          <Text size='sm' c='dimmed'>
            Decisions:
          </Text>
          {Object.entries(terminals).map(([terminal, count]) => (
            <Badge
              key={terminal}
              variant='light'
              component={Link}
              to={`${runDetailPath(kind, run.runId, 'overview')}`}
              style={{ cursor: 'pointer' }}
            >
              {terminal} {count}
            </Badge>
          ))}
        </Group>
      )}

      {run.dryRun && (
        <Text c='orange' size='sm' mb='md'>
          HubSpot merges, deletes, and review tasks were not executed in this run.
        </Text>
      )}

      {run.errorMessage && (
        <Text c='red' size='sm'>
          {run.errorMessage}
        </Text>
      )}
    </Paper>
  )
}
